import React, { useEffect, useState } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Collapse from '@mui/material/Collapse';
import SettingsService from '../../../../providers/Settings/Settings.service';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { AppSetting } from '../../../../providers/Settings/AppSettings.model';
import { redirect } from '../../../../utils/redirectUtils';
import { logRocketTrack } from '../../../../utils/logRocketUtils';
import useFeatureFlags from '../../../../providers/FeatureFlags/FeatureFlags.provider';
import './GeneralSettings.scss';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TwSwitch from '../../../../components/TwSwitch/TwSwitch';
import DriverAckSetting from './DriverAckSetting/DriverAckSetting';

export interface CardSettingProps {
  enabled: boolean;
  title: string;
  description: string;
  videoUrl?: string;
  id: string;
  readableId: string;
}

interface GeneralSettingsProps {
  category: string | null;
}

const CardSetting: React.FC<CardSettingProps> = ({
  enabled,
  title,
  description,
  videoUrl,
  id,
  readableId
}): JSX.Element => {
  const {
    data: { userData },
    updateTenantSetting
  } = useAuth();
  const { addNotification } = useNotifications();
  const { enableDriverAcknowledge } = useFeatureFlags();

  const [isToogle, setIsToogle] = useState<boolean>(
    readableId === 'driver-dispatch-ack' ? enabled : false
  );
  const [isEnabled, setIsEnable] = useState<boolean>(enabled);

  const handleClick = (): void => {
    setIsToogle(!isToogle);
  };

  const handleChange = async (): Promise<void> => {
    try {
      await SettingsService.updateSettings({
        settings: [{ id, enabled: !isEnabled }]
      });

      updateTenantSetting({ [readableId]: !isEnabled });
      setIsEnable(!isEnabled);

      // If the setting is enabled, we need to expand the card
      if (readableId === 'driver-dispatch-ack' && !isEnabled === true) setIsToogle(true);

      addNotification({
        message: 'Setting updated successfully',
        variant: 'success'
      });
    } catch (err) {
      logRocketTrack('Setting Management', {
        action: 'Update app setting',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error while updating setting',
        variant: 'error'
      });
    }
  };

  const handleVideoClick = (e: any): void => {
    e.preventDefault();
    videoUrl && redirect(videoUrl, true);
  };

  return (
    <Collapse className="card-setting" in={isToogle} collapsedSize={60} timeout={300}>
      <div className="card-setting__head">
        <TwSwitch checked={isEnabled} onChange={handleChange} label={title} />
        <div className="card-setting__icon">
          {isToogle ? (
            <KeyboardArrowUpIcon onClick={handleClick} />
          ) : (
            <KeyboardArrowDownIcon onClick={handleClick} />
          )}
        </div>
      </div>
      <div
        className={`${
          readableId === 'driver-dispatch-ack'
            ? 'card-setting__body--driver-ack'
            : 'card-setting__body'
        }`}
      >
        <div className={videoUrl ? 'card-setting__detail' : ''}>
          <p className="card-setting__title">Description</p>
          <p className="card-setting__description">{description}</p>
        </div>
        {videoUrl && (
          <div className="card-setting__video">
            <p className="card-setting__title">How I can use this?</p>
            <div onClick={handleVideoClick}>
              <iframe
                src={videoUrl}
                frameBorder="0"
                allowFullScreen
                className="card-setting__video-content"
              ></iframe>
            </div>
          </div>
        )}
        {readableId === 'driver-dispatch-ack' && enableDriverAcknowledge && (
          <DriverAckSetting isEnabled={isEnabled} />
        )}
      </div>
    </Collapse>
  );
};

const GeneralSettings: React.FC<GeneralSettingsProps> = ({ category }): JSX.Element => {
  const {
    data: { userData },
    updateTenantSetting
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { enableFactoring, enableRateTable, enableDriverAcknowledge } = useFeatureFlags();
  const [settings, setSettings] = useState<AppSetting[]>([]);

  async function disableRateTable(settingId: string, readableId: string): Promise<void> {
    await SettingsService.updateSettings({
      settings: [{ id: settingId, enabled: false }]
    });

    updateTenantSetting({ [readableId]: false });
  }

  useEffect(() => {
    (async (): Promise<void> => {
      const settings = await SettingsService.getAppSettings();
      const useRateTableSetting = settings.find(
        (setting) => setting.readableId === 'use_rate_table_for_orders'
      );
      // app settings return all, we are removing divisions and owner operators from here since there is a specific tab for them
      const generalSettings = settings.filter((setting) => {
        if (
          setting.name === 'Divisions' ||
          setting.name === 'Owner Operators' ||
          setting.name === 'Multiple carriers'
        )
          return false;

        if (!enableRateTable && setting.readableId === 'use_rate_table_for_orders') return false;
        if (enableFactoring && setting.readableId === 'enable-factoring-notice-of-assignment')
          return false;
        if (enableLtl && setting.readableId === 'brokered-loads') return false;
        if (!enableDriverAcknowledge && setting.readableId === 'driver-dispatch-ack') return false;
        return setting.category === category;
      });

      setSettings(generalSettings);
      // Disable rate table if it is enabled and the feature flag is disabled
      if (!enableRateTable && useRateTableSetting) {
        await disableRateTable(useRateTableSetting.id, useRateTableSetting.readableId);
      }
    })();
  }, [enableLtl, enableRateTable]);

  return (
    <Box
      className="app-settings__container"
      sx={category === null ? { justifyContent: 'center' } : {}}
    >
      <Box className="tab__title-wrapper">
        <Typography variant="h5" className="tab__title">
          {category === 'driver' ? 'Driver' : 'App settings'}
        </Typography>
        {category === 'driver' && (
          <Typography className="tab__description"> Manage your driver settings</Typography>
        )}
      </Box>
      <Box className="card-setting__container">
        {settings.length > 0 &&
          settings.map((setting) => (
            <CardSetting
              key={setting.id}
              enabled={setting.enabled}
              title={setting.name}
              description={setting.description}
              videoUrl={setting?.videoUrl}
              id={setting.id}
              readableId={setting.readableId}
            />
          ))}
      </Box>
    </Box>
  );
};

export default GeneralSettings;
